import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RoleRequire from "../../helpers/auth/RoleRequire";
import { useRefreshEquipments } from "../../hooks/useRefreshStateHooks/useRefreshEquipments";
import { useAppStore } from "../../stores/RootStore";
import { CategoriesType, FilterType } from "../../types/equipmentTypes";

import { EquipmentsTableData } from "../../types/formDataTypes";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input/Input";
import Filter from "./EquipmentsFilters";

import { ColumnDef } from "@tanstack/react-table";
import { MenuDataTable } from "../../ui/DataTable/MenuDataTable";

export default function EquipmentsGrid({onClose}:{onClose: () => void}) {
  const { t } = useTranslation();
  const equipments = useAppStore((state) => state.equipments);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterType>({
    producers: [],
    equipment_type: [],
    division: [],
    status: [],
    sensors: ["true", "false"],
  });

  // Получение оборудования и обновление его в сторе.
  const doFetchEquipments = useRefreshEquipments();
  useEffect(() => {
    doFetchEquipments();
  }, []);

  

  let nodes = equipments?.map((equipment) => {
    return {
      id: equipment.id,
      parent_id: equipment.attributes.parent_id,
      childs: equipment.attributes.children_ids,
      inventory_number: equipment.attributes.inventory_number,
      name: equipment.attributes.name,
      equipment_model_name: equipment.attributes.equipment_model_name,
      equipment_type_name: equipment.attributes.equipment_type_name,
      producer_name: equipment.attributes.producer_name,
      factory_number: equipment.attributes.factory_number,
      division_name: equipment.attributes.division_name,
      with_sensors: String(equipment.attributes.with_sensors),
      status: equipment.attributes.status,
      parent_changed_at: equipment.attributes.parent_changed_at,
      status_changed_at: equipment.attributes.status_changed_at,
      division_changed_at: equipment.attributes.division_changed_at,
      qrcode: equipment.attributes.qrcode,
    };
  });

  const categories: CategoriesType = {
    unique_equipment_type_name: [],
    unique_producer_name: [],
    unique_division_name: [],
    unique_status: [],
    unique_sensors: [],
  };

  // Создание массивов с уникальными названиями для категорий фильтра
  if (nodes) {
    for (let i = 0; i < nodes.length; i++) {
      const elem = nodes[i];
      if (!categories.unique_equipment_type_name.includes(elem.equipment_type_name))
        categories.unique_equipment_type_name.push(elem.equipment_type_name);
      if (!categories.unique_producer_name.includes(elem.producer_name))
        categories.unique_producer_name.push(elem.producer_name);
      if (!categories.unique_division_name.includes(elem.division_name!))
        categories.unique_division_name.push(elem.division_name!);
      if (!categories.unique_status.includes(elem.status!)) categories.unique_status.push(elem.status!);
    }
  }

  // Сортировка по умолчанию
  nodes = _.sortBy(nodes, ["inventory_number"]);
  let data = { nodes };

  const handleSearch = (event: any) => {
    setSearch(event.target.value);
  };

  // Фильтрация через поиск
  data = {
    nodes: data.nodes?.filter(
      (item) =>
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.inventory_number.toLowerCase().includes(search.toLowerCase()) ||
        item.factory_number.toLowerCase().includes(search.toLowerCase())
    ),
  };

  // Фильтрация через категории
  data = {
    nodes: data.nodes?.filter(
      (item) =>
        (filter.producers.length > 0 ? filter.producers.includes(item.producer_name) : true) &&
        (filter.equipment_type.length > 0 ? filter.equipment_type.includes(item.equipment_type_name) : true) &&
        (filter.division.length > 0 ? filter.division.includes(item.division_name!) : true) &&
        (filter.status.length > 0 ? filter.status.includes(item.status!) : true) &&
        filter.sensors.includes(item.with_sensors)
    ),
  };

  const columns: ColumnDef<EquipmentsTableData>[] = [
    {
      id: "equipment_type_name",
      accessorFn: (row) => `${row.equipment_type_name}, ${row.producer_name}, ${row.equipment_model_name}`,
      header: t("equipment_name"),
      size: 1000,
      cell: (props) => (
        <div className="max-w-[420px]">
          <div className="font-medium truncate ">{props.row.original.name}</div>
          <div className="truncate ">{`${props.row.getValue("equipment_type_name")}`}</div>
        </div>
      ),
    },
    {
      header: `${t("inventory_number")}`,
      size: 100,
      minSize: 100,
      maxSize: 100,
      cell: (props) => props.row.original.inventory_number,
    },

    {
      accessorKey: "status",
      header: t("status"),
      size: 100,
      minSize: 100,
      maxSize: 100,
      cell: (props) => t(`${props.row.getValue("status")}`),
    },
  ];


  function selectEquipment(equipment: any) {
    navigate(`/data/equipment/${equipment.id}`)
    onClose()
  }

  function addEquipment() {
    navigate("/data/equipment/new")
    onClose()
  }

  return (
    <>
        <div className="flex flex-col w-full">
            <div className="flex flex-row w-full h-14 items-center gap-4 px-6 justify-between">
                <Input 
                    className="h-8"
                    placeholder={t("equipment_search")}
                    onChange={handleSearch}
                    value={search}
                    autoComplete="off"
                    width="100%" 
                />
                
                <RoleRequire role="maintainer">
                    <Button variant={"default"} size={"md"} onClick={() => addEquipment()}>{t("add")}</Button>
                </RoleRequire>
            </div>
            <Filter filterState={[filter, setFilter]} categories={categories} />
        </div>

        {!data.nodes?.length ? ( // Заглушка для отсутствия данных при поиске
        <div className="flex w-full h-full justify-center items-center">
            {t("no_data")}
        </div>
        ) : (
          <MenuDataTable route="/data/equipment" data={data.nodes} columns={columns} onClickRow={selectEquipment}/>
        )}
    </>
  )
} 
